import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { getAxiosInstance } from '../../services/axiosInstance';
import { getScimConfig } from '../../utils/envUtils';
import EntitlementsService from '../../services/EntitlementsService';
import { GenericTable, type TableColumn, type TableAction } from './GenericTable';
import { GenericSearch } from './GenericSearch';
import {
    Badge,
    Box,
    Alert,
    Text,
} from '@mantine/core';
import { DisplayNameCell } from './DisplayNameCell';
import { debugLog, debugVerboseLog } from '../../utils/debugLog';
import { LoadingState } from './LoadingState';
import {
    buildFormAttributesFromTier2Attributes,
    buildFormAttributesFromTier2AttributeBindings,
    isEntitySetFacade
} from '../../types/EntityEntitlements';

export interface SchemaAttributeTableViewProps {
    entityType: string;
    selectedEntityId?: string | null;
    deepLinkEntityType?: string | null;
    authResolved?: boolean;
    selectedEntityType?: string;
    onEntitySelect?: (entity: any) => void;
    onSearch?: (query: string, searchAttributes?: string[], expressionDialectOmitted?: boolean | null) => void;
    onAdvancedSearch?: (filters: any) => void;
    onSearchQueryChange?: (query: string) => void;
    initialSearchQuery?: string;
}

/**
 * Specialized table view for displaying schema attributes
 */
export const SchemaAttributeTableView: React.FC<SchemaAttributeTableViewProps> = ({
    entityType: _entityType,
    selectedEntityId: _selectedEntityId,
    deepLinkEntityType: _deepLinkEntityType,
    authResolved = false,
    selectedEntityType,
    onEntitySelect,
    onSearch,
    onAdvancedSearch: _onAdvancedSearch,
    onSearchQueryChange: _onSearchQueryChange,
    initialSearchQuery: _initialSearchQuery = ''
}) => {
    // Component state
    const [attributes, setAttributes] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalResults, setTotalResults] = useState(0);
    const itemsPerPage = 25;

    // Sorting state
    const [sortBy, setSortBy] = useState<string>('name');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

    // Prevent duplicate load when effect runs twice (e.g. React Strict Mode)
    const loadStartedForRef = useRef<string | null>(null);

    /** Normalize API attribute to table row; use tier2 for Required/MultiValued when API omits them. */
    const normalizeAttributes = useCallback((attributes: any[], tier2FormAttrs: { name: string; id: string; required: boolean; multiValued: boolean }[]) => {
        const byIdOrName = new Map<string, { required: boolean; multiValued: boolean }>();
        for (const fa of tier2FormAttrs) {
            const key = (fa.id ?? fa.name ?? '').toString().trim().toLowerCase();
            if (key) byIdOrName.set(key, { required: fa.required, multiValued: fa.multiValued });
            const nameKey = (fa.name ?? '').toString().trim().toLowerCase();
            if (nameKey && !byIdOrName.has(nameKey)) byIdOrName.set(nameKey, { required: fa.required, multiValued: fa.multiValued });
        }

        return attributes.map(attr => {
            const name = attr.DisplayName || attr.Name || attr.name;
            const id = attr.Id ?? attr.id;
            const type = attr.Type ?? attr.type ?? 'string';
            const keyId = (id ?? '').toString().trim().toLowerCase();
            const keyName = (name ?? '').toString().trim().toLowerCase();
            const fromTier2 = byIdOrName.get(keyId) ?? byIdOrName.get(keyName);
            const required = fromTier2 ? fromTier2.required : (attr.Required === true || attr.required === true);
            const multiValued = fromTier2 ? fromTier2.multiValued : (attr.Array === true || attr.MultiValued === true || attr.multiValued === true);
            // Return only PascalCase so merged entity (e.g. in Raw view) is not duplicated; consumers use Id/Name/Type/Required/MultiValued
            return {
                ...attr,
                Id: id,
                Name: name,
                Type: type,
                Required: required,
                MultiValued: multiValued
            };
        });
    }, []);

    // Load schema attributes
    const loadAttributes = useCallback(async (page: number = 1, searchFilter?: string) => {
        if (!authResolved || !selectedEntityType) {
            setAttributes([]);
            setTotalResults(0);
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            debugLog(`📋 SchemaAttributeTableView: Loading schema attributes for ${selectedEntityType}`);

            const scimConfig = getScimConfig();
            const api = getAxiosInstance(scimConfig.apiUrl);
            const endpoint = `/entities/schemaentity/${selectedEntityType}`;
            const startIndex = ((page - 1) * itemsPerPage) + 1;

            const params: any = {
                startIndex,
                count: itemsPerPage,
                skipReferenceExpansion: false
            };

            if (searchFilter) {
                params.filter = searchFilter;
            }

            debugLog(`🌐 SchemaAttributeTableView: Making direct API call to ${endpoint}`);

            const apiResponse = await api.get(endpoint, { params });

            const attributesData = apiResponse.data.Attributes || [];

            // Required/MultiValued often missing on schema entity Attributes refs; get from tier2 (same source as field options)
            let tier2FormAttrs: { name: string; id: string; required: boolean; multiValued: boolean }[] = [];
            try {
                const tier2 = await EntitlementsService.fetchEntityEntitlementForSchemaEntity(selectedEntityType);
                const formAttrs = tier2
                    ? (isEntitySetFacade(tier2)
                        ? buildFormAttributesFromTier2Attributes(tier2.Attributes)
                        : (buildFormAttributesFromTier2AttributeBindings(tier2.AttributeBindings) ?? buildFormAttributesFromTier2Attributes(tier2.Attributes)))
                    : [];
                tier2FormAttrs = formAttrs.map((a: any) => ({
                    name: a.name ?? '',
                    id: a.id ?? a.name ?? '',
                    required: a.required === true,
                    multiValued: a.multiValued === true
                }));
            } catch (_) {
                // tier2 optional; table still shows API name/type
            }

            const enrichedAttributes = normalizeAttributes(attributesData, tier2FormAttrs);

            setAttributes(enrichedAttributes);
            setTotalResults(enrichedAttributes.length);
            setCurrentPage(page);
        } catch (err) {
            console.error(`❌ SchemaAttributeTableView: Error loading schema attributes for ${selectedEntityType}:`, err);
            setError(err instanceof Error ? err.message : 'Failed to load schema attributes');
            setAttributes([]);
            setTotalResults(0);
        } finally {
            setLoading(false);
        }
    }, [authResolved, selectedEntityType, itemsPerPage, normalizeAttributes]);

    // Load attributes when component mounts or selectedEntityType changes
    useEffect(() => {
        if (!authResolved || !selectedEntityType) {
            loadStartedForRef.current = null;
            return;
        }
        // Avoid duplicate request when React Strict Mode double-invokes this effect
        if (loadStartedForRef.current === selectedEntityType) return;
        loadStartedForRef.current = selectedEntityType;
        loadAttributes(1);
    }, [authResolved, selectedEntityType, loadAttributes]);

    // Sort attributes based on current sort settings
    const sortedAttributes = useMemo(() => {
        if (!attributes.length) return [];

        return [...attributes].sort((a, b) => {
            let aValue: any;
            let bValue: any;

            switch (sortBy) {
                case 'name':
                    aValue = (a.Name || a.name || '').toLowerCase();
                    bValue = (b.Name || b.name || '').toLowerCase();
                    break;
                case 'type':
                    aValue = (a.Type || a.type || '').toLowerCase();
                    bValue = (b.Type || b.type || '').toLowerCase();
                    break;
                case 'required':
                    aValue = a.Required ? 1 : 0;
                    bValue = b.Required ? 1 : 0;
                    break;
                case 'multiValued':
                    aValue = a.MultiValued ? 1 : 0;
                    bValue = b.MultiValued ? 1 : 0;
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        });
    }, [attributes, sortBy, sortOrder]);

    // Handle sort
    const handleSort = useCallback((column: string) => {
        if (sortBy === column) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(column);
            setSortOrder('asc');
        }
    }, [sortBy, sortOrder]);

    // Generate columns for schema attributes
    const generateColumns = useCallback((): TableColumn<any>[] => {
        return [
            {
                key: 'name',
                label: 'Name',
                title: 'DisplayName',
                sortable: true,
                width: '200px',
                render: (attr: any, index: number) => (
                    <DisplayNameCell
                        displayName={attr.Name || attr.name}
                        id={attr.Id || attr.id}
                        index={index}
                        startIndex={(currentPage - 1) * itemsPerPage}
                    />
                )
            },
            {
                key: 'type',
                label: 'Type',
                sortable: true,
                width: '150px',
                render: (attr: any) => attr.Type || attr.type || 'N/A'
            },
            {
                key: 'required',
                label: 'Required',
                sortable: true,
                width: '100px',
                render: (attr: any) => (
                    <Badge
                        size="sm"
                        color={attr.Required ? 'red' : 'gray'}
                        variant={attr.Required ? 'filled' : 'light'}
                    >
                        {attr.Required ? 'Yes' : 'No'}
                    </Badge>
                )
            },
            {
                key: 'multiValued',
                label: 'Multi-valued',
                sortable: true,
                width: '120px',
                render: (attr: any) => (
                    <Badge
                        size="sm"
                        color={attr.MultiValued ? 'blue' : 'gray'}
                        variant={attr.MultiValued ? 'filled' : 'light'}
                    >
                        {attr.MultiValued ? 'Yes' : 'No'}
                    </Badge>
                )
            }
        ];
    }, [currentPage, itemsPerPage]);

    // Actions are now handled in detail view, not in table

    // Handle search
    const handleSearch = useCallback((query: string) => {
        debugVerboseLog(`🔍 SchemaAttributeTableView: Search: "${query}"`);
        loadAttributes(1, query);
        if (onSearch) {
            onSearch(query);
        }
    }, [loadAttributes, onSearch]);

    // Handle page changes
    const handlePageChange = useCallback((page: number) => {
        loadAttributes(page);
    }, [loadAttributes]);

    // Handle entity selection
    const handleEntitySelect = useCallback((attribute: any) => {
        debugLog(`🔍 SchemaAttributeTableView: Attribute selected:`, attribute);
        if (onEntitySelect) {
            onEntitySelect(attribute);
        }
    }, [onEntitySelect]);

    // Render sortable table
    const renderSortableTable = () => {
        if (loading) {
            return (
                <LoadingState variant="centered" minHeight={400} />
            );
        }

        if (error) {
            return (
                <Alert color="red" mb="md">
                    {error}
                </Alert>
            );
        }

        if (!sortedAttributes.length) {
            return (
                <Box p="lg" ta="center">
                    <Text size="sm" c="dimmed">
                        No schema attributes found for {selectedEntityType}
                    </Text>
                </Box>
            );
        }

        return (
            <GenericTable
                data={sortedAttributes}
                columns={generateColumns()}
                isLoading={loading}
                onRowClick={(attr) => handleEntitySelect(attr)}
                getRowKey={(attr: any) => attr.Name || attr.name || attr.Id || attr.id || 'unknown'}
                emptyMessage={`No schema attributes found for ${selectedEntityType}`}
                entityType="schemaattribute"
            />
        );
    };

    return (
        <Box>
            <Text size="lg" fw={600} mb="sm">
                Schema Attributes for {selectedEntityType}
            </Text>
            {renderSortableTable()}
        </Box>
    );
};
